import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { MfaControlsCard } from '@/components/profile/MfaControlsCard';
import { SessionsManagementCard } from '@/components/profile/sessions-management-card';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    GenerateBackupCodesResponseSchema,
} from '@anchorbase/pillar-sdk';

function SecurityContent() {
    const log = createComponentLogger('ProfileSecurityPage');
    const { user } = useAuthStore();
    const [backupCodes, setBackupCodes] = useState<string[]>([]);

    const { data: profile, isLoading: profileLoading } =
        useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await sessionSensitiveAuthApi.getProfile();
            if (response === null || response === undefined) {
                return null;
            }
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        enabled: !!user, // Only fetch if user is logged in
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const generateBackupCodesMutation = useMutation({
        mutationFn: async () => {
            const response = await authApi.generateBackupCodes();
            return GenerateBackupCodesResponseSchema.parse(response);
        },
        onSuccess: (data) => {
            setBackupCodes(data.codes);
        },
    });

    const handleCopyBackupCodes = () => {
        runInBackground(navigator.clipboard.writeText(backupCodes.join('\n')), (err) => {
            log.error('Error copying backup codes to clipboard', err);
        });
    };

    if (profileLoading) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">Security Settings</h1>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">Security Settings</h1>
            </div>

            <div className="max-w-2xl space-y-6">
                <MfaControlsCard profile={profile ?? null} />

                {/* Backup Codes */}
                {profile?.totp_enabled && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Backup Codes</CardTitle>
                            <CardDescription>
                                Use backup codes to access your account if you lose your
                                authenticator
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {backupCodes.length === 0 ? (
                                <Button
                                    onClick={() => generateBackupCodesMutation.mutate()}
                                    disabled={generateBackupCodesMutation.isPending}
                                >
                                    {generateBackupCodesMutation.isPending
                                        ? 'Generating...'
                                        : 'Generate Backup Codes'}
                                </Button>
                            ) : (
                                <div className="space-y-4">
                                    <Alert>
                                        <AlertDescription>
                                            Save these codes in a safe place. Each code can only be
                                            used once.
                                        </AlertDescription>
                                    </Alert>
                                    <div className="p-4 bg-muted rounded-lg space-y-2">
                                        {backupCodes.map((code, idx) => (
                                            <div key={idx} className="font-mono text-sm">
                                                {code}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={handleCopyBackupCodes}>Copy Codes</Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setBackupCodes([])}
                                        >
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                <SessionsManagementCard
                    mode="user"
                    showSearch={false}
                    showHeader={false}
                    title="Active Sessions"
                    description="Manage your active login sessions across different devices"
                />
            </div>

        </div>
    );
}

export default function SecurityPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <SecurityContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
